/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { logAutoLabelCreate } from 'src/core/services/telemetry/eventsNamesTemplate';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getNextAbleResults } from 'src/core/utils/batchPromises';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { getCustomTextAutoTaggingService } from 'src/customText/core/services/autoTagging/customTextAutoTaggingService';
import { AutoTaggingJobSwagger } from 'src/customText/core/services/autoTagging/customTextAutoTaggingSwagger';
import { AutoTaggingJobStore } from 'src/customText/core/stores/CustomTextAutoTaggingJobStore';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { getAutoTaggingJobOperationHandler } from 'src/customText/core/utils/autoTaggingUtils';
import { autoTaggingJobInfoSwaggerToAutoTaggingJobInfoStore } from 'src/customText/modules/handlers/customTextAutoTaggingHandlerGroup/customTextAutoTaggingHandlerGroupMappers';

export type CustomTextAutoTaggingHandlerGroup = ReturnType<typeof createCustomTextAutoTaggingHandlerGroup>;

export const createCustomTextAutoTaggingHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore, lifetime } = dependencies;
    const { serviceName } = getPortalFromUrl();

    const autoTagCustomTextModel = async ({
        documents,
        trainedModelLabel,
        extractors,
        context
    }: HandlerParams<{
        documents: CustomTextDocumentMetadata[];
        trainedModelLabel: string;
        extractors: string[];
    }>) => {
        const { name: projectName, autoTaggingJobStoreHouse, id: projectId } = rootStore.currentCustomTextProjectStore;

        let jobId: string;
        const autoTaggingService = getCustomTextAutoTaggingService({ context });

        try {
            const statusUrl = await autoTaggingService.initiateAutoTaggingJob({
                extractors,
                projectName,
                trainedModelLabel,
                documents: documents.map(doc => ({ location: doc.name, language: doc.language }))
            });
            jobId = new URL(statusUrl).pathname.split('/').pop();
            autoTaggingJobStoreHouse.add(new AutoTaggingJobStore(jobId, { status: 'inProgress', trainedModelLabel }));
            await autoTaggingService.pollAutoTagging({
                statusUrl,
                operationHandler: getAutoTaggingJobOperationHandler(lifetime, autoTaggingJobStoreHouse)
            });

            logAutoLabelCreate(serviceName, projectId);
        } catch (e) {
            if (jobId) {
                autoTaggingJobStoreHouse.findById(jobId).setStatus('failed');
            }

            throw e;
        }
    };

    const getAutotaggingQuota = async ({ projectName, context }: HandlerParams<{ projectName: string }>) => {
        const {
            customTextProjectStoreHouse,
            azureStore: {
                session: {
                    csAccountStore: { path }
                }
            }
        } = rootStore;
        const currentCustomTextProjectStore = customTextProjectStoreHouse.findById(projectName);

        const autoTaggingService = getCustomTextAutoTaggingService({ context });

        const { quota } = await autoTaggingService.getAutoTaggingJobs({ projectName, resourcePath: path });

        const quotaInChars = { totalQuota: quota?.totalQuota, consumedQuota: quota?.consumedQuota };
        currentCustomTextProjectStore.setAutoTaggingQuota(quotaInChars);
    };

    const pollCustomTextAutoTaggingJobs = async (params?: HandlerParams<object>) => {
        const {
            currentCustomTextProjectStore,
            azureStore: {
                session: {
                    csAccountStore: { path }
                }
            }
        } = rootStore;
        const { name: projectName, autoTaggingJobStoreHouse } = currentCustomTextProjectStore;

        const autoTaggingService = getCustomTextAutoTaggingService({ context: params?.context });

        autoTaggingJobStoreHouse.tracker.setState('running');

        const autoTaggingJobs = [];
        try {
            const { quota, nextLink, jobs } = await autoTaggingService.getAutoTaggingJobs({ projectName, resourcePath: path });
            autoTaggingJobs.push(...jobs);

            if (nextLink) {
                const nextAbleJobs = await getNextAbleResults<'jobs', AutoTaggingJobSwagger>(
                    (nextPageLink: string) => autoTaggingService.getAutoTaggingJobs({ nextPageLink, projectName, resourcePath: path }),
                    'jobs',
                    [],
                    nextLink
                );
                autoTaggingJobs.push(...nextAbleJobs);
            }

            // The returned quota consists of units which each unit have 1000 char.
            // The store needs to save the exact char not the units
            const quotaInChars = { totalQuota: quota?.totalQuota, consumedQuota: quota?.consumedQuota };
            currentCustomTextProjectStore.setAutoTaggingQuota(quotaInChars);
            autoTaggingJobStoreHouse.setItems(autoTaggingJobs.map(autoTaggingJobInfoSwaggerToAutoTaggingJobInfoStore));

            autoTaggingJobStoreHouse.tracker.setState('completed');
        } catch {
            autoTaggingJobStoreHouse.tracker.setState('failed');
        }
        const autoTaggingJobsInProgress = autoTaggingJobs
            .filter(j => j.status === 'notStarted' || j.status === 'running')
            .sort((j1, j2) => new Date(j2.createdDateTime).getMilliseconds() - new Date(j1.createdDateTime).getMilliseconds());

        if (autoTaggingJobsInProgress.length === 0) {
            return;
        }

        autoTaggingJobsInProgress.forEach(
            async job =>
                await autoTaggingService.pollAutoTagging({
                    projectName,
                    jobId: job.jobId,
                    operationHandler: getAutoTaggingJobOperationHandler(lifetime, autoTaggingJobStoreHouse)
                })
        );
    };

    return { getAutotaggingQuota, autoTagCustomTextModel, pollCustomTextAutoTaggingJobs };
};
